// dashboardHelpers.js — funções puras extraídas de Dashboard121Insights.jsx.
//
// Dashboard121Insights.jsx é o orquestrador principal (~4400 linhas, dezenas
// de useState/useSelector, dispatch de Redux, 9+ abas filhas) — a imensa
// maioria do arquivo está genuinamente acoplada a hooks/estado e não dá pra
// extrair sem reescrever o componente. Este arquivo reúne só os pedaços que
// eram cálculos puros (entrada -> saída, sem ler state/hooks diretamente),
// extraídos 1:1 (mesmo comportamento) pra poderem ser testados isoladamente.
import { LOADING_STATUSES } from "@constants";

// Mesma chave usada em Dashboard121Insights.jsx (PERIOD_TYPE_BY_KEY.mensal) —
// repetida aqui como default pra manter estas funções independentes de
// qualquer constante só definida dentro do componente.
const DEFAULT_MONTHLY_TYPE = "monthly";

// --- valoresAtuaisModal ------------------------------------------------------
// Monta o mapa "nome da métrica/etapa -> valor atual" usado pelo modal de
// plano de ação (preenche o baseline/valor atual ao abrir o modal a partir de
// uma etapa ou indicador geral). Overview traz os 3 indicadores gerais
// (NPS/CSAT/CES); journey traz o valor atual de cada etapa da jornada.
export function buildValoresAtuaisModal(overviewRow = {}, journeyRows = []) {
  const map = {
    "NPS Geral": overviewRow.nps ?? 0,
    "CSAT Geral": overviewRow.csat ?? 0,
    "CES Geral": overviewRow.ces ?? 0,
  };
  (journeyRows || []).forEach((e) => {
    if (e?.name) map[e.name] = e.value ?? 0;
  });
  return map;
}

// --- Dashboard loading/error state -------------------------------------------
// Deriva se o dashboard ainda está no carregamento inicial (qualquer seção
// obrigatória ausente ou "idle") e qual é o primeiro erro, dentre as seções
// obrigatórias, a mostrar pro usuário.
export function computeDashboardLoadingState(
  dashboardState,
  requiredKeys,
  idleStatus = LOADING_STATUSES.IDLE,
) {
  const isInitialLoading = requiredKeys.some((key) => {
    const section = dashboardState?.[key];
    return !section || section.status === idleStatus;
  });

  const dashboardError =
    requiredKeys.map((key) => dashboardState?.[key]?.error).filter(Boolean)[0] ||
    null;

  return { isInitialLoading, dashboardError };
}

// --- respondents_monthly: série pro gráfico mensal ---------------------------
// Filtra só linhas "monthly" (trimestral/semestral/anual não têm carga
// própria nessa fonte — ver buildRespondentsByPeriod), ordena por data
// (com fallback pro nome do período quando period_start_date falta) e
// mantém só os últimos 12 meses.
export function buildRespondentsMonthlyChartData(
  respondentsMonthlyRows = [],
  monthlyType = DEFAULT_MONTHLY_TYPE,
) {
  return respondentsMonthlyRows
    .filter((item) => item.period_type === monthlyType)
    .sort((a, b) =>
      String(
        a.period_start_date || a.year_month || a.period_name,
      ).localeCompare(
        String(b.period_start_date || b.year_month || b.period_name),
      ),
    )
    .slice(-12)
    .map((item) => ({
      m: item.period_name || item.year_month,
      r: Number(item.total_respondents) || 0,
      period_start_date: item.period_start_date,
    }));
}

// --- respondentsByPeriod: agregação trimestral/semestral/anual ---------------
// respondents_monthly só tem linhas "monthly" — trimestral/semestral/anual
// são agregados aqui a partir dos meses reais mais recentes (em vez dos
// rótulos fixos herdados de PER, que mostravam sempre o mesmo período).
// `formatPeriodLabel` é a mesma função usada por PeriodPillBig
// (periodPillBig.helpers.js), passada por fora pra não duplicar a lógica de
// rótulo nem criar uma segunda fonte de verdade pra formatação de período.
const AGG_BUCKET_KEY = {
  trimestral: (d) => `${d.getFullYear()}-T${Math.floor(d.getMonth() / 3) + 1}`,
  semestral: (d) => `${d.getFullYear()}-S${d.getMonth() < 6 ? 1 : 2}`,
  anual: (d) => `${d.getFullYear()}`,
};
const AGG_LABEL = {
  trimestral: (d) => {
    const t = Math.floor(d.getMonth() / 3) + 1;
    const yy = String(d.getFullYear()).slice(-2);
    return { short: `T${t}/${yy}`, label: `T${t} ${d.getFullYear()}` };
  },
  semestral: (d) => {
    const s = d.getMonth() < 6 ? 1 : 2;
    const yy = String(d.getFullYear()).slice(-2);
    return { short: `S${s}/${yy}`, label: `S${s} ${d.getFullYear()}` };
  },
  anual: (d) => ({
    short: `${d.getFullYear()}`,
    label: `Ano ${d.getFullYear()}`,
  }),
};

export function buildRespondentsByPeriod({
  periods,
  respondentsMonthlyRows = [],
  formatPeriodLabel,
  monthlyType = DEFAULT_MONTHLY_TYPE,
}) {
  const monthlyRowsAsc = respondentsMonthlyRows
    .filter((item) => item.period_type === monthlyType && item.period_start_date)
    .map((item) => ({
      date: new Date(item.period_start_date),
      resp: Number(item.total_respondents) || 0,
    }))
    .sort((a, b) => a.date - b.date);
  const latestMonthRow = monthlyRowsAsc[monthlyRowsAsc.length - 1] || null;

  return periods.reduce((acc, key) => {
    if (key === "mensal") {
      const periodName =
        latestMonthRow &&
        respondentsMonthlyRows.find(
          (item) =>
            item.period_type === monthlyType &&
            new Date(item.period_start_date).getTime() ===
              latestMonthRow.date.getTime(),
        )?.period_name;

      acc[key] = {
        label: "",
        short: "",
        ondas: 1,
        ...(periodName
          ? {
              label: formatPeriodLabel(monthlyType, periodName),
              short: periodName,
            }
          : {}),
        resp: latestMonthRow ? latestMonthRow.resp : null,
      };
      return acc;
    }

    if (!latestMonthRow) {
      acc[key] = { label: "", short: "", ondas: null, resp: null };
      return acc;
    }

    const bucketOf = AGG_BUCKET_KEY[key];
    const targetBucket = bucketOf(latestMonthRow.date);
    const rowsInBucket = monthlyRowsAsc.filter(
      (r) => bucketOf(r.date) === targetBucket,
    );
    const { short, label } = AGG_LABEL[key](latestMonthRow.date);

    acc[key] = {
      label,
      short,
      ondas: rowsInBucket.length,
      resp: rowsInBucket.reduce((s, r) => s + r.resp, 0),
    };
    return acc;
  }, {});
}

// --- openPublishWizard: ganho sugerido ao publicar -------------------------
// Ganho "automático" sugerido pro wizard de publicação no CooperAção: usa o
// ganho já registrado no sucesso da ação, ou (se a ação não tem sucesso
// registrado ainda) a diferença simples entre valor atual e baseline, nunca
// negativa.
export function calcGanhoSugerido(acao) {
  const ganho = acao?.sucesso
    ? acao.sucesso.ganhoPp || 0
    : Math.max(0, (Number(acao?.v_atual) || 0) - (Number(acao?.v_baseline) || 0));
  return Math.round(ganho);
}
